import type { ReviewTicker, UserInstrumentDto } from "@eu.mdotm.api/sphere";
import { generateUniqueDOMId } from "@mdotm/shadcn/legacy/react-extensions";
import BigNumber from "bignumber.js";
import { isRowBlocked } from "$root/ui-lib/table/custom-column/instrument/blocking-issue";
import { sumArrayLike } from "$root/utils";
import type { ReviewGridRow, ReviewInstrument } from "./const";

/** One review-composition entry as the review grid shows it, named by the user's instrument metadata. */
export function toReviewInstrument(instrument: ReviewTicker, userInstrument: UserInstrumentDto): ReviewInstrument {
	return {
		...userInstrument,
		name: userInstrument.name ?? `Instrument ${userInstrument.identifiers?.ALIAS ?? instrument.alias}`,
		alias: instrument.alias ?? userInstrument.identifiers?.ALIAS,
		identifiers: {
			...(userInstrument.identifiers ?? {}),
			ALIAS: instrument.alias ?? userInstrument.identifiers?.ALIAS,
		},
		delisted: instrument.delisted ?? false,
		hasBeenReviewed: hasBeenReviewed(instrument),
		needsCustomProxy: instrument.needsCustomProxy,
		weight: instrument.weight ?? 0,
		proxies: instrument.proxies,
		ticker: userInstrument.tickerName ?? instrument.ticker ?? instrument.alias,
		deleted: hasBeenDeleted(instrument),
		instrumentType: instrument.proxyOverwriteType === "PORTFOLIO_MIXED" ? "INVESTMENT" : "INSTRUMENT",
		investmentUuid:
			instrument.proxyOverwriteType === "PORTFOLIO_MIXED"
				? (userInstrument.tickerName ?? instrument.ticker)
				: undefined,
		currency: instrument.currency,
		issue: instrument.issue,
		proxyOverwriteType: instrument.proxyOverwriteType ?? userInstrument.proxyOverwriteType,
		id: generateUniqueDOMId(),
	};
}

/**
 * Where a row sorts in the review grid: the ones that need a review first, then the blocked ones, then
 * the rest. A blocked row sorts with the blocked ones whatever issue it also carries.
 */
export function reviewSortRank(row: Pick<ReviewGridRow, "issue" | "delisted">): number {
	if (isRowBlocked(row)) {
		return 1;
	}
	const needToReview =
		row.issue === "MISSING_RISK_MODEL" ||
		row.issue === "RISK_MODEL_SUGGESTION_REQUIRES_REVIEW" ||
		row.issue === "UNRECOGNIZED_INSTRUMENT" ||
		row.issue === "NOT_SUPPORTED_BOND" ||
		row.issue === "MISSING_CLOSES";
	return needToReview ? 2 : 0;
}

export function hasBeenReviewed(instrument: ReviewTicker): boolean | undefined {
	if (!instrument.issue) {
		return undefined;
	}
	return false;
}

export function hasBeenDeleted(instrument: ReviewTicker): boolean {
	return isRowBlocked(instrument);
}

export function needsReview(
	instrument: Pick<ReviewGridRow, "issue" | "proxies" | "deleted" | "hasBeenReviewed">,
): boolean {
	if (instrument.issue === "RISK_MODEL_SUGGESTION_REQUIRES_REVIEW") {
		const sum = BigNumber(sumArrayLike(instrument.proxies ?? [], (y) => y.weight ?? 0)).decimalPlaces(3);
		return !instrument.deleted && sum.isLessThan(100);
	}

	const issueToResolve =
		instrument.issue === "UNRECOGNIZED_INSTRUMENT" ||
		instrument.issue === "MISSING_RISK_MODEL" ||
		instrument.issue === "NOT_SUPPORTED_BOND" ||
		instrument.issue === "MISSING_CLOSES";

	return !instrument.deleted && issueToResolve && !instrument.hasBeenReviewed;
}

export function sanitizeReviewInstrument(instrument: Pick<ReviewGridRow, "deleted">): boolean {
	return !instrument.deleted;
}

export function convertReviewInstrumentToReviewTicker(instrument: ReviewInstrument): ReviewTicker {
	let ticker: string | undefined = instrument.ticker ?? instrument.tickerName ?? instrument.alias;
	let alias = instrument.alias;

	if (instrument.issue === "UNRECOGNIZED_INSTRUMENT" && instrument.newAlias) {
		ticker = undefined;
		alias = instrument.newAlias;
	}

	return {
		ticker,
		alias,
		needsCustomProxy: instrument.needsCustomProxy,
		proxies: instrument.proxies,
		weight: instrument.weight,
		proxyOverwriteType: instrument.proxyOverwriteType,
		previousWeight: instrument.previousWeight,
		currency: instrument.currency,
	};
}
